// Best-effort local mirror of folder/card data + card images, so a user who has already
// viewed a deck online can still open and study it with no network at all. This is never
// a hard dependency for the online path — every function here is fire-and-forget and
// swallows its own errors, matching the try/catch convention already used for this app's
// localStorage writes (e.g. AppHome.jsx's `localStorage.setItem('ff:sort', ...)`).
//
// Three object stores:
//   folders     — mirror of the folder list (id, name, color, cardCount, flashNotes, ...)
//   folderCards — one row per folder: { folderId, cards: [...], cachedAt }
//   images      — card images as blobs, keyed by their Storage download URL

const DB_NAME = 'flashfonic-offline';
const DB_VERSION = 1;

let dbPromise = null;

function isIndexedDbAvailable() {
    return typeof indexedDB !== 'undefined';
}

async function openOfflineDb() {
    if (!isIndexedDbAvailable()) return null;
    if (!dbPromise) {
        dbPromise = import('idb').then(({ openDB }) =>
            openDB(DB_NAME, DB_VERSION, {
                upgrade(db) {
                    if (!db.objectStoreNames.contains('folders')) {
                        db.createObjectStore('folders', { keyPath: 'id' });
                    }
                    if (!db.objectStoreNames.contains('folderCards')) {
                        db.createObjectStore('folderCards', { keyPath: 'folderId' });
                    }
                    if (!db.objectStoreNames.contains('images')) {
                        db.createObjectStore('images', { keyPath: 'imageUrl' });
                    }
                },
            })
        ).catch(() => null);
    }
    return dbPromise;
}

export async function cacheFolderList(folders) {
    try {
        const db = await openOfflineDb();
        if (!db || !Array.isArray(folders)) return;
        const tx = db.transaction('folders', 'readwrite');
        await Promise.all(folders.map((f) => tx.store.put({ ...f, cards: undefined })));
        await tx.done;
    } catch (_) { /* best-effort cache — never block the online path */ }
}

export async function getCachedFolderList() {
    try {
        const db = await openOfflineDb();
        if (!db) return [];
        return await db.getAll('folders');
    } catch (_) {
        return [];
    }
}

async function cacheImageForCard(imageUrl) {
    try {
        if (!imageUrl) return;
        const db = await openOfflineDb();
        if (!db) return;
        const existing = await db.get('images', imageUrl);
        if (existing) return;
        const res = await fetch(imageUrl);
        if (!res.ok) return;
        const blob = await res.blob();
        await db.put('images', { imageUrl, blob, cachedAt: Date.now() });
    } catch (_) { /* image caching is opportunistic — a miss just means no offline image */ }
}

export async function cacheFolderCards(folderId, cards) {
    try {
        const db = await openOfflineDb();
        if (!db || !folderId || !Array.isArray(cards)) return;
        await db.put('folderCards', { folderId, cards, cachedAt: Date.now() });
        // Fire-and-forget image caching — don't make callers wait on every image fetch.
        cards.filter((c) => c.imageUrl).forEach((c) => { cacheImageForCard(c.imageUrl); });
    } catch (_) { /* best-effort cache — never block the online path */ }
}

export async function getCachedFolderCards(folderId) {
    try {
        const db = await openOfflineDb();
        if (!db || !folderId) return [];
        const row = await db.get('folderCards', folderId);
        return row?.cards || [];
    } catch (_) {
        return [];
    }
}

// Returns a local object URL for offline <img> rendering, or null if never cached.
export async function getCachedImageUrl(imageUrl) {
    try {
        if (!imageUrl) return null;
        const db = await openOfflineDb();
        if (!db) return null;
        const row = await db.get('images', imageUrl);
        if (!row?.blob) return null;
        return URL.createObjectURL(row.blob);
    } catch (_) {
        return null;
    }
}
